/**
 * Client-side North Indian chart layout — VedAstro's own `NorthIndianChart`
 * SVG draws the grid correctly but omits rashi (sign) numbers and uses full
 * planet names instead of the classical two-letter codes. This module
 * recomputes the house/rashi layout locally from already-fetched planet and
 * house data so the studio can overlay proper numbers + abbreviations on
 * top of VedAstro's grid artwork.
 *
 * Divisional (varga) house placement needs the chart's OWN ascendant sign —
 * not just the D1 Lagna — since e.g. the Navamsha lagna is a different sign
 * from the Rasi lagna. That comes straight from the engine: `AllHouseData`
 * house 1's `House<Varga>Sign` IS that varga's Ascendant. An earlier revision
 * of this module claimed those fields returned duplicate values across vargas
 * and re-derived everything locally instead; that was re-tested on three live
 * charts and is false — the 16 fields give 8 distinct signs, D9 and D10 differ,
 * and every one matches the classical formula applied to the Ascendant. Using
 * them directly avoids re-deriving a varga from an already-rounded longitude,
 * and covers all 16 charts rather than 12.
 *
 * `VARGA_LAGNA_FORMULAS` therefore serves only as a fallback for when the
 * field is missing. Its formulas were each verified against VedAstro's own
 * `AllPlanetData` varga output for all 9 grahas (zero mismatches); the four
 * without a verified formula (D2, D7, D30, D60) rely on the engine's field.
 *
 * When falling back, the Ascendant degree is house 1's `middle` — the Bhava
 * Madhya. NOT `begin`/`end`, which are the Bhava Sandhi junctions ~15° to
 * either side and round into the wrong sign whenever the Ascendant sits in
 * the first half of its sign, silently rotating the whole chart by one house.
 */

import { SIGN_NAMES, signIndex, houseFrom, PLANET_ABBR } from './gochara.js'

const MOVABLE = [0, 3, 6, 9] // Aries, Cancer, Libra, Capricorn
const FIXED = [1, 4, 7, 10] // Taurus, Leo, Scorpio, Aquarius
const FIRE = [0, 4, 8], EARTH = [1, 5, 9], AIR = [2, 6, 10], WATER = [3, 7, 11]

const modality = (s) => (MOVABLE.includes(s) ? 'movable' : FIXED.includes(s) ? 'fixed' : 'dual')
const element = (s) => (FIRE.includes(s) ? 'fire' : EARTH.includes(s) ? 'earth' : AIR.includes(s) ? 'air' : 'water')
// 0-based sign index is even <=> the sign's classical (1-based) position is odd.
const isOddSign = (s) => s % 2 === 0

/** Verified (against live AllPlanetData for all 9 grahas) classical varga formulas. */
const VARGA_LAGNA_FORMULAS = {
  D1: (s) => s,
  D3: (s, deg) => (s + [0, 4, 8][Math.floor(deg / 10)]) % 12,
  D4: (s, deg) => (s + [0, 3, 6, 9][Math.floor(deg / 7.5)]) % 12,
  D9: (s, deg) => {
    const m = modality(s)
    const start = m === 'movable' ? s : m === 'fixed' ? (s + 8) % 12 : (s + 4) % 12
    return (start + Math.floor(deg / (30 / 9))) % 12
  },
  D10: (s, deg) => {
    const start = isOddSign(s) ? s : (s + 8) % 12
    return (start + Math.floor(deg / 3)) % 12
  },
  D12: (s, deg) => (s + Math.floor(deg / 2.5)) % 12,
  D16: (s, deg) => {
    const m = modality(s)
    const start = m === 'movable' ? 0 : m === 'fixed' ? 4 : 8 // Aries / Leo / Sagittarius
    return (start + Math.floor(deg / 1.875)) % 12
  },
  D20: (s, deg) => {
    const m = modality(s)
    const start = m === 'movable' ? 0 : m === 'fixed' ? 8 : 4 // Aries / Sagittarius / Leo
    return (start + Math.floor(deg / 1.5)) % 12
  },
  D24: (s, deg) => {
    const start = isOddSign(s) ? 4 : 3 // Leo / Cancer
    return (start + Math.floor(deg / 1.25)) % 12
  },
  D27: (s, deg) => {
    const e = element(s)
    const start = e === 'fire' ? 0 : e === 'earth' ? 3 : e === 'air' ? 6 : 9 // Aries/Cancer/Libra/Capricorn
    return (start + Math.floor(deg / (30 / 27))) % 12
  },
  D40: (s, deg) => {
    const start = isOddSign(s) ? 0 : 6 // Aries / Libra
    return (start + Math.floor(deg / 0.75)) % 12
  },
  D45: (s, deg) => {
    const m = modality(s)
    const start = m === 'movable' ? 0 : m === 'fixed' ? 4 : 8
    return (start + Math.floor(deg / (30 / 45))) % 12
  },
}

/** Vargas the local fallback formula covers. The engine's own field covers all 16. */
export const SUPPORTED_VARGAS = Object.keys(VARGA_LAGNA_FORMULAS)

/** "NavamshaD9" → "D9" (matches the short keys used in a planet's `vargas` map). */
export function vargaShortKey(chartTypeKey) {
  const m = /D(\d+)$/.exec(chartTypeKey || '')
  return m ? `D${m[1]}` : null
}

/** Sidereal absolute longitude (degrees, 0–360) → that varga's Ascendant sign name, or null if unsupported. */
export function vargaLagnaSign(ascendantLongitude, shortKey) {
  const fn = VARGA_LAGNA_FORMULAS[shortKey]
  if (!fn || !Number.isFinite(ascendantLongitude)) return null
  const norm = ((ascendantLongitude % 360) + 360) % 360
  const signIdx = Math.floor(norm / 30)
  const degInSign = norm - signIdx * 30
  return SIGN_NAMES[fn(signIdx, degInSign)]
}

/* ─────────────────────────── Chart geometry ───────────────────────────
 * VedAstro's grid is a 0..1000 viewBox, but the playing square is INSET by a
 * decorative border — it is not the full 1000×1000 box. The real square was
 * read off the SVG's own structural paths: the left mid-edge vertex sits at
 * `M76.04 495.9` and the right at `M947.32 495.99l4.99.05`, giving
 * x ∈ [76, 952] and a true centre of (514, 500). Decorative marks along the
 * diagonals (`M233.46 215.73`, `M275.37,257.53`, `M288.07 723.47`) confirm
 * the diagonal/diamond crossings land on the quarter points.
 *
 * VedAstro's own planet anchors ([475,150], [250,400], …) are consistently
 * up-and-left of these true cell centres, which is why labels drawn at them
 * looked off-balance — we derive our own from the geometry instead.
 */
const CX = 514, CY = 500, Q = 219 // centre + quarter-span (half of the half-width)

// Where each house's graha stack is centred. Rhombi (1/4/7/10) use their own
// midpoint and the tall side triangles (3/5/9/11) their true centroid — both
// have room to spare. The four flat triangles (2/6/8/12) are only ~219 units
// tall, so a strict centroid (a third of the way up from the base) leaves the
// stack lopsided: it would overrun the chart's outer edge at one end while
// leaving a gap at the other. Those four instead centre on the band left free
// between their rashi number and the outer edge.
export const HOUSE_CENTER = {
  1: [CX, CY - Q], 4: [CX - Q, CY], 7: [CX, CY + Q], 10: [CX + Q, CY],
  3: [149, 281], 5: [149, 719], 9: [879, 719], 11: [879, 281],
  2: [295, 141], 12: [733, 141], 6: [295, 852], 8: [733, 852],
}

// Where each house's rashi number goes: tucked at the cell's INNER vertex —
// the corner it shares with the chart centre or one of the four diagonal
// crossings — nudged just inside its own cell. This is the classical
// placement (and what AstroSage does): the four central houses' numbers ring
// the middle point, and each pair of corner triangles brackets its junction,
// so a number is never mistaken for a neighbouring house's.
// `[x, y, textAnchor]`.
export const HOUSE_NUMBER = {
  1: [CX, 448, 'middle'], 7: [CX, 556, 'middle'], // above / below centre
  4: [482, 514, 'end'], 10: [546, 514, 'start'], // left / right of centre
  2: [295, 239, 'middle'], 3: [265, 290, 'end'], // top-left junction
  12: [733, 239, 'middle'], 11: [765, 290, 'start'], // top-right junction
  6: [295, 761, 'middle'], 5: [265, 712, 'end'], // bottom-left junction
  8: [733, 761, 'middle'], 9: [765, 712, 'start'], // bottom-right junction
}

/**
 * Display colours for the nine grahas, tuned for the studio's cream chart
 * ground. Hue families follow printed-kundli convention (green Mars, blue
 * Mercury, magenta Jupiter, black-family Saturn, smoky Rahu, golden Ketu —
 * the palette the client's AstroSage reference uses) but all nine stay
 * mutually distinguishable, where the reference prints Su/Sa/Ra in three
 * near-identical reds.
 */
export const PLANET_COLORS = {
  Sun: '#c93a2e',
  Moon: '#5878a8',
  Mars: '#2e7d32',
  Mercury: '#2b6cb0',
  Jupiter: '#a83a9c',
  Venus: '#0f766e',
  Saturn: '#37415c',
  Rahu: '#5d4037',
  Ketu: '#a0761d',
}

/** House 1's diamond, for a soft tint that singles out the Lagna cell. */
export const LAGNA_POLYGON = `${CX},${CY - 2 * Q} ${CX - Q},${CY - Q} ${CX},${CY} ${CX + Q},${CY - Q}`

/**
 * Type scale + column count for a house's graha stack. A cell can hold
 * anything from one graha to all nine, so the text shrinks as the stack
 * grows. Past four, a single column would outgrow the cell vertically — the
 * four "flat" corner triangles (2/6/8/12) are only ~219 units tall — so
 * crowded houses switch to two columns, which is also how printed charts set
 * a stellium: wider is cheaper than taller in every cell of this grid.
 */
export function planetLayout(count) {
  if (count <= 3) return { fontSize: 34, lineHeight: 46, columns: 1, columnGap: 0 }
  if (count === 4) return { fontSize: 30, lineHeight: 40, columns: 1, columnGap: 0 }
  if (count <= 6) return { fontSize: 28, lineHeight: 36, columns: 2, columnGap: 46 }
  return { fontSize: 24, lineHeight: 31, columns: 2, columnGap: 40 }
}

/**
 * Baseline positions for a house's grahas, as a block centred on the cell's
 * centre. A trailing odd graha centres itself on its own row rather than
 * hanging off to one side. The +0.34em nudge turns the block's centre line
 * into a text baseline, which sits below the glyphs' optical middle.
 */
export function planetPositions([cx, cy], count) {
  const { fontSize, lineHeight, columns, columnGap } = planetLayout(count)
  const rows = Math.ceil(count / columns)
  return Array.from({ length: count }, (_, i) => {
    const row = Math.floor(i / columns)
    const inRow = Math.min(columns, count - row * columns)
    const col = i - row * columns
    return {
      x: cx + (col - (inRow - 1) / 2) * columnGap,
      y: cy - ((rows - 1) * lineHeight) / 2 + row * lineHeight + fontSize * 0.34,
      fontSize,
    }
  })
}

/**
 * Build the 12 fixed North-Indian houses for one varga, given the chart's
 * Ascendant longitude and each planet's sign in that varga. Returns null
 * when the varga has no verified formula (see module doc) — the caller
 * should fall back to VedAstro's plain (unnumbered) chart in that case.
 */
export function buildVargaHouses({ ascendantLongitude, vargaLagna, planets, shortKey }) {
  // Prefer the engine's own divisional Ascendant (AllHouseData house 1's
  // `House<Varga>Sign`). It needs no re-derivation from a rounded longitude,
  // and it covers all 16 vargas — including D2/D7/D30/D60, which have no
  // verified local formula. Fall back to the classical formula only if the
  // field is absent.
  const lagnaSign = vargaLagna || vargaLagnaSign(ascendantLongitude, shortKey)
  if (!lagnaSign) return null
  const lagnaIdx = signIndex(lagnaSign)
  const houses = Array.from({ length: 12 }, (_, i) => {
    const rashiIdx = (lagnaIdx + i) % 12
    return {
      house: i + 1,
      rashi: rashiIdx + 1,
      sign: SIGN_NAMES[rashiIdx],
      center: HOUSE_CENTER[i + 1],
      numberPos: HOUSE_NUMBER[i + 1],
      planets: [],
    }
  })
  for (const p of planets) {
    const v = p.vargas?.[shortKey]
    const h = houseFrom(v?.sign, lagnaSign)
    if (h)
      houses[h - 1].planets.push({
        name: p.name,
        abbr: PLANET_ABBR[p.name] || p.name.slice(0, 2),
        // Whole degrees the planet has travelled into this sign (0–29), from
        // the varga field's own DegreesIn — used for the superscript label.
        deg: Number.isFinite(v?.deg) ? Math.floor(v.deg) : null,
      })
  }
  return { lagnaSign, houses }
}
